import type { ImageLoaderProps } from "next/dist/shared/lib/image-config";
import { IPageLink } from "@/sanity/globals/CTA";
import { IPopup } from "@/sanity/globals/Popups";
import { PortableTextBlock } from "sanity";

export interface GROQImage {
  src: string;
  width: number;
  height: number;
  lqip: string;
  name: string;
}

export interface GROQCTA {
  name: string;
  title?: string;
  icon?: string;
  action: "link" | "popup" | "anchor";
  link?: IPageLink;
  popup?: IPopup;
  anchor?: string;
}

export interface GROQButton {
  type?: "default" | "purple" | "outline" | "outlineBlur" | "lightGreen" | "lightYellow" | "white" | "yellow" | "darkGlow" | "lightGlow";
  cta?: GROQCTA;
}

export interface GROQMenuItem {
  title: string;
  titleDE?: string;
  type: "single" | "dropdown";
  link?: IPageLink;
  dropdownLinks?: Array<{
    icon?: GROQImage;
    title: string;
    titleDE?: string;
    link: IPageLink;
  }>;
}

export interface GROQMenu {
  name: string;
  items: Array<GROQMenuItem>;
  // cta
  popupTexts: PortableTextBlock[];
  formId: string;
  navbarCTA: GROQButton;
  navbarCTA2?: GROQButton;
}

export interface GROQHeader {
  menu: GROQMenu;
}

export interface IHeader {
  name: string;
  menu: {
    title: string;
    link?: IPageLink;
    isCategory: boolean;
    subItems?: {
      title: string;
      description?: string;
      icon?: GROQImage;
      link: IPageLink;
    }[];
    socialSubItems?: {
      image: GROQImage;
      title: string;
      link: IPageLink;
    }[];
  }[];
  headerCTA: GROQButton;
}

export interface IFooter {
  name: string;
  menuItems: {
    title: string;
    link: IPageLink;
  }[];
  riskDisclosure: PortableTextBlock[];
}

export interface ITrustpilotTestimonial {
  name: string;
  testimonials: {
    image: GROQImage;
    name: string;
    position: string;
    text: PortableTextBlock[];
  }[];
}

export interface ITraders {
  name: string;
  items: {
    image: GROQImage;
    text: PortableTextBlock[];
  }[];
}

export interface ITopBanner {
  name: string;
  ctaLink: string;
  de: {
    text: string;
    ctaText: string;
  };
  en: {
    text: string;
    ctaText: string;
  };
  br: {
    text: string;
    ctaText: string;
  };
}

export interface GROQMuxVideo {
  playbackId: string;
  assetId: string;
  filename: string;
  status: string;
}

export function GROQImageLoader({
  src,
  width,
  quality = 90,
}: ImageLoaderProps): string {
  return src + "?auto=format&q=" + quality;
}

export function GROQUnoptimizedImageLoader({
  src,
  width,
  quality = 75,
}: ImageLoaderProps): string {
  return src + "?q=100";
}

export const GROQImageSchema =
  '{"src": asset->url, "width": asset->metadata.dimensions.width, "height": asset->metadata.dimensions.height, "lqip": asset->metadata.lqip, "name": asset->originalFilename}';

export const GROQPageLinkSchema =
  "{link,externalLink,openInNewTab,url,anchor,internalLink->{settings{slug{current}}}}";

export const HubSpotFormSchema = `
{
  _id,
  name,
  fields,
  settings{
    onSubmit,
    submitText,
    message,
    redirect${GROQPageLinkSchema},
    portalId,
    formId,
  }
}
`;

export const ZapierFormSchema = `
{
  _id,
  name,
  fields,
  settings{
    onSubmit,
    submitText,
    message,
    redirect${GROQPageLinkSchema},
    url,
  }
}
`;

export const GROQPopupSchemaQuery = `{
  name,
  theme,
  content,
  additionalContent,
  formType,
  hsForm->${HubSpotFormSchema},
  zapierForm->${ZapierFormSchema},
}`;

export const GROQFormSchema = `{
  _id,
  name,
  fields,
  settings{
    onSubmit,
    submitText,
    message,
    redirect${GROQPageLinkSchema},
    portalId,
    formId,
  }
}`;

export const GROQPageCTASchema = `{
  name,
  title,
  icon,
  action,
  anchor,
  link${GROQPageLinkSchema},
  popup->${GROQPopupSchemaQuery}
}`;

export const GROQButtonSchema = `{
  type,
  cta->${GROQPageCTASchema}
}`;

export const GROQMuxVideoShema = `{
  "playbackId": asset->playbackId,
  "assetId": asset->assetId,
  "filename": asset->filename,
  "status": asset->status
}`;

export const MuxVideoPlayerSchema = `{
  embed,
  muxVideo${GROQMuxVideoShema},
  embedUrl,
  thumbnail${GROQImageSchema}
}`;

export const GROQMenuSchema = `{
  name,
  navbarCTA${GROQButtonSchema},
  navbarCTA2${GROQButtonSchema},
  popupTexts,
  items[]{
    title,
    titleDE,
    type,
    link${GROQPageLinkSchema},
    dropdownLinks[]{
      icon${GROQImageSchema},
      title,
      titleDE,
      link${GROQPageLinkSchema},
    },
  },
}`;

export const GROQHeaderMenuSchema = `{menu->}`;
export const GROQHeader = `{
  menu->${GROQMenuSchema},
}`;

export const HeaderSchema = `->{
  name,
  menu[]{
    title,
    link${GROQPageLinkSchema},
    isCategory,
    subItems[]{
      title,
      description,
      icon${GROQImageSchema},
      link${GROQPageLinkSchema},
    },
    socialSubItems[]{
      image${GROQImageSchema},
      title,
      link${GROQPageLinkSchema},
    },
  },
  headerCTA${GROQButtonSchema},
}`;

export const FooterSchema = `->{
  name,
  menuItems[]{
    title,
    link${GROQPageLinkSchema},
  },
  riskDisclosure,
}`;

export const TrustpilotTestimonialsSchema = `{
  name,
  testimonials[]{
    image${GROQImageSchema},
    name,
    position,
    text,
  },
}`;

export const TradersSchema = `{
  name,
  items[]{
    image${GROQImageSchema},
    text,
  },
}`;

export const TopBannerSchema = `->{
  name,
  ctaLink,
  de{
    text,
    ctaText,
  },
  en{
    text,
    ctaText,
  },
  br{
    text,
    ctaText,
  },
}`;
